import React, { useState, useEffect } from 'react';
import { Plus, DollarSign as DollarSignIcon, Filter } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import CreateSubscriptionModal from '../components/CreateSubscriptionModal';
import BillsTable from '../components/BillsTable';
import RevenueChart from '../components/RevenueChart';
import subscriptionService from '../services/subscriptionService';
import billService, { Bill, BillStatus } from '../services/billService';

const GestaoDizimos: React.FC = () => {
  const [bills, setBills] = useState<Bill[]>([]);
  const [filteredBills, setFilteredBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | BillStatus>('all');
  const [viewMode, setViewMode] = useState<'dashboard' | 'bills'>('dashboard');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredBills(bills);
    } else {
      setFilteredBills(bills.filter(bill => bill.status === statusFilter));
    }
  }, [statusFilter, bills]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const billsData = await billService.getBills();
      console.log('GestaoDizimos - billsData:', billsData);
      setBills(billsData);
      setFilteredBills(billsData);
    } catch (err) {
      console.error('GestaoDizimos - Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async (subscriptionData: any) => {
    try {
      await subscriptionService.createSubscription(subscriptionData);
      await loadData();
      setIsCreateModalOpen(false);
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateBillStatus = async (id: number, status: BillStatus) => {
    try {
      await billService.updateBill(id, { status });
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao atualizar cobrança');
    }
  };

  const handleUploadProof = async (id: number, file: File) => {
    try {
      await billService.uploadProofPayment(id, file);
      await loadData();
      alert('Comprovante enviado com sucesso!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao enviar comprovante');
    }
  };

  const handleViewProof = async (id: number) => {
    try {
      const url = await billService.getProofPaymentUrl(id);
      window.open(url, '_blank');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao buscar comprovante');
    }
  };

  const handleDeleteBill = async (id: number) => {
    try {
      await billService.deleteBill(id);
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao excluir cobrança');
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar currentPath="/dizimos" />

      <div className="flex-1 ml-0 md:ml-64 pt-14 md:pt-0">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-3">
                <DollarSignIcon size={32} className="text-gold-600" />
                <h1 className="text-2xl md:text-3xl font-bold text-brown-800">Gestão de Cobranças</h1>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors shadow-md text-sm md:text-base"
              >
                <Plus size={20} />
                Criar Assinatura
              </button>
            </div>
            <p className="text-gray-600">
              Gerencie assinaturas, cobranças e acompanhe a previsibilidade das receitas.
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <div className="flex gap-4">
              <button
                onClick={() => setViewMode('dashboard')}
                className={`px-4 py-2 font-medium transition-colors ${
                  viewMode === 'dashboard'
                    ? 'text-gold-600 border-b-2 border-gold-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setViewMode('bills')}
                className={`px-4 py-2 font-medium transition-colors ${
                  viewMode === 'bills'
                    ? 'text-gold-600 border-b-2 border-gold-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Cobranças
              </button>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gold-500"></div>
              <p className="mt-4 text-gray-600">Carregando...</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {/* Content */}
          {!loading && !error && (
            <>
              {viewMode === 'dashboard' && <RevenueChart bills={bills} />}

              {viewMode === 'bills' && (
                <div className="space-y-6">
                  {/* Filtros */}
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center gap-4">
                      <Filter size={20} className="text-gray-600" />
                      <label className="text-sm font-medium text-gray-700">
                        Filtrar por status:
                      </label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as any)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                      >
                        <option value="all">Todas</option>
                        <option value="pending">Pendentes</option>
                        <option value="paid">Pagas</option>
                        <option value="overdue">Vencidas</option>
                        <option value="cancelled">Canceladas</option>
                      </select>
                      <span className="text-sm text-gray-600">
                        {filteredBills.length} {filteredBills.length === 1 ? 'cobrança' : 'cobranças'}
                      </span>
                    </div>
                  </div>

                  {/* Tabela */}
                  <BillsTable
                    bills={filteredBills}
                    onUpdateStatus={handleUpdateBillStatus}
                    onUploadProof={handleUploadProof}
                    onViewProof={handleViewProof}
                    onDelete={handleDeleteBill}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      <CreateSubscriptionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubscription}
      />
    </div>
  );
};

export default GestaoDizimos;
